import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

const generateCompulsoryBalanceAccountExcel = async (data, selectedBranches = [], selectedDate = null) => {
    try {
        if (!data || !Array.isArray(data)) {
            throw new Error("Invalid data format");
        }

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Compulsory Balance Account Analysis");

        sheet.mergeCells('A1:I1');
        sheet.getCell('A1').value = "Compulsory Balance Account Analysis";
        sheet.getCell('A1').font = { bold: true, size: 16 };
        sheet.getCell('A1').alignment = { horizontal: 'center' };

        if (selectedDate) {
            sheet.mergeCells('A2:I2');
            sheet.getCell('A2').value = `Audit Date: ${selectedDate}`;
            sheet.getCell('A2').font = { bold: true };
            sheet.getCell('A2').alignment = { horizontal: 'center' };
        }

        if (selectedBranches.length > 0) {
            sheet.mergeCells('A3:I3');
            sheet.getCell('A3').value = `Selected Branches: ${selectedBranches.join(', ')}`;
            sheet.getCell('A3').alignment = { horizontal: 'center' };
        }

        sheet.mergeCells('A4:I4');
        const returnedBranches = [...new Set(data.map(item => item.branchName))];
        sheet.getCell('A4').value = `Branches with Data: ${returnedBranches.join(', ')} (${returnedBranches.length} of ${selectedBranches.length} branches)`;
        sheet.getCell('A4').font = { color: { argb: 'FF0000' }, italic: true };
        sheet.getCell('A4').alignment = { horizontal: 'center' };

        sheet.addRow([]);

        // Headers
        const headers = [
            'Branch Name',
            'Branch ID',
            'Deposit Type',
            'Individual Accounts',
            'Individual Rate %',
            'Joint Accounts',
            'Joint Rate %',
            'Collection Amount',
            'Period Type'
        ];

        const headerRow = sheet.addRow(headers);
        headerRow.eachCell(cell => {
            cell.font = { bold: true, color: { argb: 'FFFFFF' } };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '2F75B5' }
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };
        });

        // Data rows
        let totalIndividual = 0;
        let totalJoint = 0;
        let totalCollection = 0;

        // Create a map of returned data by branch name for easier lookup
        const dataByBranch = {};
        data.forEach(item => {
            dataByBranch[item.branchName] = item;
        });

        // Show data for all selected branches, mark missing ones
        selectedBranches.forEach(branchName => {
            const item = dataByBranch[branchName];

            if (item) {
                // Branch has data
                const individualCount = item.individualCount || 0;
                const jointCount = item.jointCount || 0;
                const collectionAmount = item.collectionAmount || 0;

                totalIndividual += individualCount;
                totalJoint += jointCount;
                totalCollection += collectionAmount;

                const rowData = [
                    item.branchName || 'N/A',
                    item.branchId || 'N/A',
                    item.depositType || 'N/A',
                    individualCount,
                    item.individualRate ? `${(item.individualRate * 100).toFixed(2)}%` : '0.00%',
                    jointCount,
                    item.jointRate ? `${(item.jointRate * 100).toFixed(2)}%` : '0.00%',
                    collectionAmount,
                    item.periodType || 'As of the audit date'
                ];

                const row = sheet.addRow(rowData);
                row.eachCell(cell => {
                    cell.border = {
                        top: { style: 'thin' }, left: { style: 'thin' },
                        bottom: { style: 'thin' }, right: { style: 'thin' }
                    };

                    // Format numbers
                    if (cell.col === 4 || cell.col === 6) {
                        cell.numFmt = '#,##0';
                        cell.alignment = { horizontal: 'right' };
                    } else if (cell.col === 8) {
                        cell.numFmt = '#,##0.00';
                        cell.alignment = { horizontal: 'right' };
                    } else if (cell.col === 5 || cell.col === 7) {
                        cell.alignment = { horizontal: 'center' };
                    }
                });
            } else {
                // Branch has no data - show as empty/missing
                const rowData = [
                    branchName,
                    'N/A',
                    'COMPULSORY SAVINGS',
                    0,
                    '0.00%',
                    0,
                    '0.00%',
                    0,
                    'No data available'
                ];

                const row = sheet.addRow(rowData);
                row.eachCell(cell => {
                    cell.border = {
                        top: { style: 'thin' }, left: { style: 'thin' },
                        bottom: { style: 'thin' }, right: { style: 'thin' }
                    };
                    cell.font = { italic: true, color: { argb: '999999' } };

                    // Format numbers
                    if (cell.col === 4 || cell.col === 6) {
                        cell.numFmt = '#,##0';
                        cell.alignment = { horizontal: 'right' };
                    } else if (cell.col === 8) {
                        cell.numFmt = '#,##0.00';
                        cell.alignment = { horizontal: 'right' };
                    } else if (cell.col === 5 || cell.col === 7) {
                        cell.alignment = { horizontal: 'center' };
                    }
                });
            }
        });

        sheet.addRow([]);
        const summaryRow = sheet.addRow([
            'TOTAL',
            '',
            '',
            totalIndividual,
            '',
            totalJoint,
            '',
            totalCollection,
            ''
        ]);

        summaryRow.eachCell(cell => {
            cell.font = { bold: true };
            if (cell.col === 4 || cell.col === 6 || cell.col === 8) {
                if (cell.col === 8) {
                    cell.numFmt = '#,##0.00';
                } else {
                    cell.numFmt = '#,##0';
                }
                cell.alignment = { horizontal: 'right' };
            }
        });

        // Set column widths
        sheet.getColumn(1).width = 20;
        sheet.getColumn(2).width = 12;
        sheet.getColumn(3).width = 25;
        sheet.getColumn(4).width = 18;
        sheet.getColumn(5).width = 15;
        sheet.getColumn(6).width = 15;
        sheet.getColumn(7).width = 12;
        sheet.getColumn(8).width = 18;
        sheet.getColumn(9).width = 20;

        // Generate filename
        const branchName = selectedBranches.length > 0 ?
            (selectedBranches.length === 1 ? selectedBranches[0] : 'Multiple_Branches') :
            'All_Branches';
        const dateStr = selectedDate ? selectedDate.replace(/-/g, '') : dayjs().format('YYYYMMDD');
        const fileName = `Compulsory_Balance_Account_Analysis_${branchName}_${dateStr}.xlsx`;

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(blob, fileName);
        return true;

    } catch (error) {
        console.error("Error generating Excel:", error);
        throw error;
    }
};

export default generateCompulsoryBalanceAccountExcel;